import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export type ActivationOutcome =
  | 'seatGranted'
  | 'seatReclaimed'
  | 'seatReleased'
  /** An administrator took the seat back from the Seat sessions page. */
  | 'seatRevoked'
  | 'seatDenied'
  | 'authFailed'
  | 'rejected';

/**
 * One recorded event. Note what is absent: no device name, no IP address, no raw device id.
 * `deviceHash` is stable within a calendar month and meaningless outside it.
 */
export interface ActivationLogEntry {
  createdAt: string;
  outcome: ActivationOutcome;
  statusCode: number;
  failureReason?: string;
  licenseId?: string;
  activationId?: string;
  sessionId?: string;
  packageIdentifier?: string;
  deviceHash?: string;
  deviceModel?: string;
  runtimePlatform?: string;
  version?: string;
  /** How long the seat had been held. Present on release and reclaim only. */
  heldSeconds?: number;
}

export interface ActivationLogPage {
  items: ActivationLogEntry[];
  total: number;
  page: number;
  limit: number;
}

interface FamilyCounts { supar: number, composer: number }

export interface ActivationLogSummary {
  from: string;
  to: string;
  /** Null when nothing has been sampled in the window — not the same as zero usage. */
  peakConcurrent: FamilyCounts | null;
  currentInUse: FamilyCounts | null;
  entitled: FamilyCounts;
  averageHeldSeconds: number | null;
  closedSessions: number;
  refusalsByReason: Record<string, number>;
}

export interface SeatUsagePoint {
  at: string;
  supar: number;
  composer: number;
}

export interface ActivationLogQuery {
  from?: string;
  to?: string;
  licenseId?: string;
  outcome?: ActivationOutcome;
  failureReason?: string;
  page?: number;
  limit?: number;
}

const toParams = (query: ActivationLogQuery) =>
  Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== ''));

export const activationLogApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getActivationLogs: builder.query<ActivationLogPage, ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs', params: toParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
    getActivationLogSummary: builder.query<ActivationLogSummary, ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs/summary', params: toParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
    getSeatUsageSeries: builder.query<SeatUsagePoint[], ActivationLogQuery>({
      query: (params) => ({ url: '/v1/activation-logs/series', params: toParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const {
  useGetActivationLogsQuery,
  useGetActivationLogSummaryQuery,
  useGetSeatUsageSeriesQuery,
} = activationLogApiSlice;
